import { ref, onMounted, watch, computed } from 'vue'
import { useAuth } from './useAuth'
import api from '../api/axios'
import { locationLog, locationWarn } from '../utils/locationDebug'


interface LocationTrackingData {
  latitude: number
  longitude: number
  accuracy: number
  altitude: number | null
  speed: number | null
  heading: number | null
  trackable_type: string
  trackable_id: number
  timestamp: string
  battery_level: number | null
  is_moving: boolean
  activity_type: string
  metadata: {
    weather: string
    road_type: string
    source: string
  }
}

interface LocationTrackingState {
  isActive: boolean
  lastSent: number | null
  error: string | null
  intervalId: number | null
}

const trackingState = ref<LocationTrackingState>({
  isActive: false,
  lastSent: null,
  error: null,
  intervalId: null
})

// Global variables for tracking outside of Vue components
let isTrackingActive = false
let trackingInterval: NodeJS.Timeout | null = null
let lastLocationUpdate: Date | null = null

// Retry state. A failed start used to be silent and final: retryTracking() was
// only called when retryCount > 0, and retryCount only ever grew inside a retry
// — so the first failure never produced a second attempt. One GPS timeout at
// login cost a whole shift of data.
let consecutiveStartFailures = 0
let lastStartFailureReason: string | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
const MAX_RETRIES = 3
const RETRY_BASE_DELAY = 10000 // 10s, then 20s, then 40s

// How often a position is collected. Positions are flushed every 2nd tick.
const COLLECT_INTERVAL_MS = 30000

// Watchdog: last line of defence against a tracking run that is technically
// alive but no longer producing anything (dead timer, GPS silently failing).
const WATCHDOG_INTERVAL_MS = 60000
const STALE_POSITION_MS = 90000 // 3 missed collects
let watchdogInterval: ReturnType<typeof setInterval> | null = null

// In-flight start guard: startBackgroundTracking() is called from many places
// (App.vue resume/init, LoginView, TerminalSelectionView, the auth watchers).
// Without this, two calls racing inside the initial getCurrentPosition() await
// (up to 15s) each installed their own interval — the 6x duplicate rows.
let startPromise: Promise<boolean> | null = null

// Bumped by stopBackgroundTracking(). A start that was already awaiting GPS when
// a stop came in must not install its interval afterwards (zombie interval).
let stopGeneration = 0

// Last position timestamp we accepted. The device may hand back the very same
// cached fix twice in a row; those must not become two rows.
let lastAcceptedTimestamp: string | null = null

// Batch buffer: collect positions and flush every 2nd tick (60 seconds)
const positionBuffer: LocationTrackingData[] = []
const MAX_BUFFER_SIZE = 20
let isFlushing = false

// Global auth state for use outside Vue components
let globalAuthState = {
  isAuthenticated: false,
  user: null as any
}

// Function to update global auth state (called from Vue components)
export const updateGlobalAuthState = (authState: any) => {
  locationLog('🔄 Updating global auth state:', {
    isAuthenticated: authState?.isAuthenticated || false,
    user: authState?.user ? { id: authState.user.id, name: authState.user.name } : null
  })
  
  globalAuthState = {
    isAuthenticated: authState?.isAuthenticated || false,
    user: authState?.user || null
  }
  
  locationLog('✅ Global auth state updated:', {
    isAuthenticated: globalAuthState.isAuthenticated,
    user: globalAuthState.user ? { id: globalAuthState.user.id, name: globalAuthState.user.name } : null
  })
}

// Function to check if user is authenticated (simplified - just check token)
const isUserAuthenticated = (): boolean => {
  const token = localStorage.getItem('token')
  const hasToken = !!token
  
  // Check both token and global auth state
  const isAuthenticated = hasToken && globalAuthState.isAuthenticated && globalAuthState.user
  
  locationLog('🔍 Auth check:', {
    hasToken,
    globalAuthState: globalAuthState.isAuthenticated,
    hasUser: !!globalAuthState.user,
    result: isAuthenticated
  })
  
  return isAuthenticated
}

// Function to get user data from global auth state
const getUserData = () => {
  return globalAuthState.user
}

// Get battery level if available
const getBatteryLevel = async (): Promise<number | null> => {
  try {
    if ('getBattery' in navigator) {
      // @ts-ignore - getBattery is not in the standard types yet
      const battery = await navigator.getBattery()
      return battery.level * 100
    }
  } catch (error) {
    locationLog('Battery API not available or failed:', error)
  }
  return null
}

// Determine if user is moving based on speed
const getIsMoving = (speed: number | null): boolean => {
  return speed !== null && speed > 1.0 // Consider moving if speed > 1 m/s
}

// Determine activity type based on speed
const getActivityType = (speed: number | null): string => {
  if (speed === null) return 'unknown'
  if (speed < 1.0) return 'stationary'
  if (speed < 5.0) return 'walking'
  if (speed < 15.0) return 'running'
  return 'driving'
}

// Get road type based on speed and context
const getRoadType = (speed: number | null): string => {
  if (speed === null) return 'unknown'
  if (speed > 25.0) return 'highway'
  if (speed > 15.0) return 'main_road'
  if (speed > 5.0) return 'local_road'
  return 'pedestrian'
}

// Maximum acceptable accuracy in meters (100km - to handle very poor GPS)
const MAX_ACCURACY = 100000

// Validate and sanitize location data before sending
const validateLocationData = (locationData: LocationTrackingData): LocationTrackingData => {
  // Cap accuracy at maximum value to prevent database overflow
  if (locationData.accuracy > MAX_ACCURACY) {
    locationWarn(`⚠️ Accuracy ${locationData.accuracy}m exceeds maximum, capping at ${MAX_ACCURACY}m`)
    locationData.accuracy = MAX_ACCURACY
  }
  
  // Ensure accuracy is a valid number
  if (isNaN(locationData.accuracy) || locationData.accuracy < 0) {
    locationWarn('⚠️ Invalid accuracy value, setting to maximum')
    locationData.accuracy = MAX_ACCURACY
  }
  
  // Round accuracy to 2 decimal places
  locationData.accuracy = Math.round(locationData.accuracy * 100) / 100
  
  return locationData
}

// Send location data to server
const sendLocationToServer = async (locationData: LocationTrackingData): Promise<boolean> => {
  try {
    // Validate and sanitize data before sending
    const validatedData = validateLocationData(locationData)
    
    locationLog('🔄 Sending location data to server:', {
      lat: validatedData.latitude,
      lng: validatedData.longitude,
      accuracy: validatedData.accuracy,
      timestamp: validatedData.timestamp,
      userId: validatedData.trackable_id
    })
    
    const response = await api.post('/api/location-tracking/store', validatedData)
    
    if (response.status === 200 || response.status === 201) {
      locationLog('✅ Location data sent successfully')
      trackingState.value.lastSent = Date.now()
      trackingState.value.error = null
      return true
    } else {
      throw new Error(`Server responded with status: ${response.status}`)
    }
  } catch (error) {
    console.error('❌ Error sending location data:', error)
    
    // Log more details about the error
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      })
    }
    
    // Don't set error state for network issues, just log them
    if (error && typeof error === 'object' && 'code' in error) {
      locationLog('Network error code:', (error as any).code)
    }
    
    // Handle different error types
    if (error && typeof error === 'object' && 'response' in error) {
      const response = (error as any).response
      
      // Don't treat 500 errors as failures - they're backend issues, not location permission issues
      if (response && response.status >= 500) {
        locationWarn('⚠️ Server error (5xx) - location works but storage failed')
        // Don't set error state - location is working
        return false
      }
      
      if (response && response.status >= 400 && response.status < 500) {
        // Client error (4xx) - might be authentication issue
        trackingState.value.error = error instanceof Error ? error.message : 'Unknown error'
      }
    }
    
    return false
  }
}

// Send a batch of location data to server
const sendBatchToServer = async (positions: LocationTrackingData[]): Promise<boolean> => {
  if (positions.length === 0) return true
  try {
    const validated = positions.map(p => validateLocationData({ ...p }))

    locationLog(`Sending batch of ${validated.length} positions to server`)

    const response = await api.post('/api/location-tracking/store-batch', { positions: validated })

    if (response.status === 200 || response.status === 201) {
      locationLog(`Batch of ${validated.length} positions sent successfully`)
      trackingState.value.lastSent = Date.now()
      trackingState.value.error = null
      return true
    } else {
      throw new Error(`Server responded with status: ${response.status}`)
    }
  } catch (error) {
    console.error('Error sending batch location data:', error)

    if (error && typeof error === 'object' && 'response' in error) {
      const response = (error as any).response
      if (response && response.status >= 400 && response.status < 500) {
        trackingState.value.error = error instanceof Error ? error.message : 'Unknown error'
      }
    }

    return false
  }
}

// Flush the position buffer to the batch endpoint
const flushPositionBuffer = async (): Promise<void> => {
  if (isFlushing || positionBuffer.length === 0) return
  isFlushing = true
  try {
    const toSend = positionBuffer.splice(0, positionBuffer.length)
    const success = await sendBatchToServer(toSend)
    if (!success) {
      // Put positions back (up to max) for retry on next flush
      const spaceLeft = MAX_BUFFER_SIZE - positionBuffer.length
      if (spaceLeft > 0) {
        positionBuffer.unshift(...toSend.slice(0, spaceLeft))
      }
    }
  } finally {
    isFlushing = false
  }
}

// Create location tracking data object
const createLocationData = async (
  position: GeolocationPosition,
  userId: number
): Promise<LocationTrackingData> => {
  const speed = position.coords.speed
  const isMoving = getIsMoving(speed)
  const activityType = getActivityType(speed)
  const roadType = getRoadType(speed)
  const batteryLevel = await getBatteryLevel()
  
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    altitude: position.coords.altitude,
    speed: speed,
    heading: position.coords.heading,
    trackable_type: 'App\\Models\\User',
    trackable_id: userId,
    timestamp: new Date(position.timestamp).toISOString(),
    battery_level: batteryLevel,
    is_moving: isMoving,
    activity_type: activityType,
    metadata: {
      weather: 'unknown', // Could be enhanced with weather API
      road_type: roadType,
      source: 'mobile_app'
    }
  }
}

// Function to get current location using browser API directly
const getCurrentLocationDirect = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'))
      return
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => reject(error),
      {
        enableHighAccuracy: false, // Use false for better battery life and background tracking
        timeout: 15000, // Increase timeout to 15 seconds
        // Must stay well below COLLECT_INTERVAL_MS. With maximumAge === the poll
        // interval the device kept returning the identical cached fix, producing
        // duplicate rows with an identical timestamp.
        maximumAge: 5000
      }
    )
  })
}

// Accept a position only once. Returns false when the device handed back the
// very same cached fix we already recorded.
const acceptPosition = (locationData: LocationTrackingData): boolean => {
  if (lastAcceptedTimestamp !== null && locationData.timestamp === lastAcceptedTimestamp) {
    locationLog('Duplicate position timestamp, skipping:', locationData.timestamp)
    return false
  }
  lastAcceptedTimestamp = locationData.timestamp
  return true
}

// Clear the collect interval, if any.
const clearTrackingInterval = (): void => {
  if (trackingInterval) {
    clearInterval(trackingInterval)
    trackingInterval = null
  }
}

// Start background location tracking (only if not already running).
// Kept as a separate export because several views call it by name; the guard
// now lives in startBackgroundTracking() itself, so both are equivalent.
export const startBackgroundTrackingIfNotRunning = async (): Promise<boolean> => {
  return startBackgroundTracking()
}

// Start background location tracking.
// Idempotent: concurrent or repeated calls never produce a second interval.
export const startBackgroundTracking = async (): Promise<boolean> => {
  if (isTrackingActive && trackingInterval !== null) {
    locationLog('🔄 Background tracking already active, skipping start')
    return true
  }

  if (startPromise) {
    locationLog('🔄 Background tracking start already in flight, joining it')
    return startPromise
  }

  startPromise = performStartBackgroundTracking()
  try {
    return await startPromise
  } finally {
    startPromise = null
  }
}

// The actual start sequence. Never call directly — go through
// startBackgroundTracking() so the in-flight guard applies.
const performStartBackgroundTracking = async (): Promise<boolean> => {
  locationLog('🔍 Starting startBackgroundTracking function...')

  // Snapshot the stop counter: if a stop happens while we await GPS below,
  // this start must abandon instead of installing an orphaned interval.
  const generation = stopGeneration

  // Use global state instead of calling useAuth() directly
  if (!isUserAuthenticated()) {
    locationLog('🚫 User not authenticated, cannot start location tracking')
    locationLog('🔍 Current auth state:', {
      isAuthenticated: isUserAuthenticated(),
      user: getUserData() ? { id: getUserData().id, name: getUserData().name } : null
    })

    // Deliberately no retry here: this is not a GPS problem. Once the user logs
    // in, the auth watcher starts tracking. Retrying would only put the app into
    // an error state while sitting on the login screen.
    return false
  }

  locationLog('✅ User is authenticated, proceeding with tracking setup...')

  // From here on a user is logged in, so the health check should be running —
  // even if this very start fails, the watchdog and the resume listener are
  // what recover us.
  startTrackingWatchdog()
  attachResumeListeners()
  
  try {
    locationLog('🚀 Starting background location tracking for user:', getUserData().id)
    
    // Get initial position using direct API
    locationLog('📍 Getting initial position...')
    const initialPosition = await getCurrentLocationDirect()
    if (!initialPosition) {
      locationLog('❌ Could not get initial position')
      scheduleRetry('Keine Position vom Gerät erhalten')
      return false
    }
    
    locationLog('📍 Initial position obtained:', {
      lat: initialPosition.coords.latitude,
      lng: initialPosition.coords.longitude,
      accuracy: initialPosition.coords.accuracy
    })

    // A stop (logout, permission loss) landed while we were waiting for GPS.
    if (generation !== stopGeneration) {
      locationLog('🛑 Tracking was stopped while starting, abandoning this start')
      return false
    }

    // Send initial location
    const locationData = await createLocationData(initialPosition, getUserData().id)

    if (generation !== stopGeneration) {
      locationLog('🛑 Tracking was stopped while starting, abandoning this start')
      return false
    }

    if (acceptPosition(locationData)) {
      // Health is measured by "did we get a fix", not "did the upload succeed" —
      // otherwise the watchdog would keep restarting a perfectly good run while
      // the driver is offline on the apron.
      lastLocationUpdate = new Date()

      locationLog('📤 Sending initial location to server...')
      const initialSuccess = await sendLocationToServer(locationData)

      if (initialSuccess) {
        locationLog('✅ Initial location sent successfully')
      } else {
        locationLog('⚠️ Failed to send initial location, but continuing with tracking')
      }
    }

    if (generation !== stopGeneration) {
      locationLog('🛑 Tracking was stopped while starting, abandoning this start')
      return false
    }

    // Never leave a previous interval behind — there can only ever be one.
    clearTrackingInterval()

    // Set up interval for periodic location updates
    // Collect a position every 30 seconds, flush the batch every 60 seconds
    locationLog('Setting up interval for periodic updates (30s collect, 60s flush)...')
    let tickCount = 0
    const intervalId = setInterval(async () => {
      try {
        // Hard safety net: only the currently registered interval may collect.
        // Any stray interval unregisters itself on its next tick.
        if (trackingInterval !== intervalId) {
          locationLog('Stale tracking interval detected, clearing it')
          clearInterval(intervalId)
          return
        }

        // Check current auth state in interval
        if (!isUserAuthenticated()) {
          locationLog('User no longer authenticated, stopping tracking')
          stopBackgroundTracking()
          return
        }

        const position = await getCurrentLocationDirect()
        if (position) {
          const locationData = await createLocationData(position, getUserData().id)

          if (acceptPosition(locationData)) {
            // Add to buffer, enforce max size
            if (positionBuffer.length >= MAX_BUFFER_SIZE) {
              positionBuffer.shift()
            }
            positionBuffer.push(locationData)
            lastLocationUpdate = new Date()

            locationLog(`Position buffered (${positionBuffer.length} in buffer)`)
          }
        }

        // Flush every 2nd tick (60 seconds)
        tickCount++
        if (tickCount >= 2) {
          tickCount = 0
          await flushPositionBuffer()
        }
      } catch (error) {
        console.error('Error in location tracking interval:', error)
      }
    }, COLLECT_INTERVAL_MS)

    locationLog('⏰ Interval set up with ID:', intervalId)
    
    // Update tracking state
    trackingState.value.isActive = true
    trackingState.value.error = null
    isTrackingActive = true
    trackingInterval = intervalId
    resetRetryState() // A successful start clears the failure chain

    locationLog('🎉 Background location tracking started successfully with interval ID:', intervalId)
    locationLog('📊 Final state: isTrackingActive =', isTrackingActive, ', intervalId =', trackingInterval)
    return true
    
  } catch (error) {
    console.error('❌ Error starting background location tracking:', error)

    // Retry on EVERY failure, not just on retries — this is the underground
    // garage / briefly denied permission case that used to cost a whole shift.
    scheduleRetry(describeStartFailure(error))

    return false
  }
}

// Turn a geolocation failure into something a driver could act on.
const describeStartFailure = (error: unknown): string => {
  const code = (error as GeolocationPositionError | undefined)?.code
  if (code === 1) return 'Standortzugriff verweigert'
  if (code === 2) return 'Kein GPS-Signal'
  if (code === 3) return 'GPS antwortet nicht'
  return error instanceof Error ? error.message : 'Ortung konnte nicht gestartet werden'
}

// Stop background location tracking
export const stopBackgroundTracking = (): void => {
  locationLog('Stopping background location tracking...')

  // Invalidate any start that is currently awaiting GPS, so it cannot install
  // its interval after we stopped.
  stopGeneration++

  clearTrackingInterval()

  // Best-effort flush of remaining buffered positions
  if (positionBuffer.length > 0 && !isFlushing) {
    flushPositionBuffer().catch(() => {
      // Silently discard — user is leaving, token may already be cleared
    })
  }

  // Drop any pending retry — whoever stopped us decides what happens next.
  resetRetryState()

  // Update both local and global state
  trackingState.value.isActive = false
  isTrackingActive = false

  locationLog('Background location tracking stopped')
}

// Snapshot of the tracking internals. Used by tests and by the UI status bar.
export interface TrackingStatus {
  isActive: boolean
  hasInterval: boolean
  isStarting: boolean
  isHealthy: boolean
  bufferedPositions: number
  lastPositionAt: Date | null
  lastSentAt: number | null
  error: string | null
  failedStarts: number
  failureReason: string | null
  retryPending: boolean
  watchdogRunning: boolean
}

export const getTrackingStatus = (): TrackingStatus => ({
  isActive: isTrackingActive,
  hasInterval: trackingInterval !== null,
  isStarting: startPromise !== null,
  isHealthy: isTrackingHealthy(),
  bufferedPositions: positionBuffer.length,
  lastPositionAt: lastLocationUpdate,
  lastSentAt: trackingState.value.lastSent,
  error: trackingState.value.error,
  failedStarts: consecutiveStartFailures,
  failureReason: lastStartFailureReason,
  retryPending: retryTimer !== null,
  watchdogRunning: watchdogInterval !== null
})

// Cancel a pending retry, e.g. because tracking was stopped or succeeded.
const cancelRetry = (): void => {
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
}

// Reset everything the retry chain accumulated.
const resetRetryState = (): void => {
  cancelRetry()
  consecutiveStartFailures = 0
  lastStartFailureReason = null
}

// Schedule the next start attempt after a failed one.
// Unlike the old retryTracking(), this fires on the FIRST failure too, backs off
// (10s / 20s / 40s) and never stacks more than one pending timer.
const scheduleRetry = (reason: string): void => {
  lastStartFailureReason = reason
  consecutiveStartFailures++
  trackingState.value.error = reason

  if (retryTimer) {
    locationLog('🔄 A retry is already pending, not scheduling another')
    return
  }

  if (consecutiveStartFailures > MAX_RETRIES) {
    // Give up on the fast chain; the watchdog keeps trying once a minute so a
    // driver who leaves the underground garage recovers without re-login.
    locationLog(`❌ ${consecutiveStartFailures} failed starts, handing over to the watchdog`)
    return
  }

  const delay = RETRY_BASE_DELAY * Math.pow(2, consecutiveStartFailures - 1)
  locationLog(`🔄 Retrying location tracking in ${delay}ms (attempt ${consecutiveStartFailures}/${MAX_RETRIES}): ${reason}`)

  retryTimer = setTimeout(() => {
    retryTimer = null
    if (!isUserAuthenticated()) {
      locationLog('❌ User no longer authenticated, dropping retry chain')
      resetRetryState()
      return
    }
    // A failure inside schedules the next attempt itself.
    startBackgroundTracking()
  }, delay)
}

// Is tracking actually producing data, or only nominally running?
const isTrackingHealthy = (): boolean => {
  if (!isTrackingActive || trackingInterval === null) {
    return false
  }
  if (lastLocationUpdate === null) {
    return false
  }
  return Date.now() - lastLocationUpdate.getTime() <= STALE_POSITION_MS
}

// Bring tracking back into a healthy state. Safe to call from anywhere and as
// often as you like — it starts nothing that is already running well.
export const ensureTrackingHealthy = async (): Promise<boolean> => {
  if (!isUserAuthenticated()) {
    stopTrackingWatchdog()
    return false
  }

  // A start is in flight; let it finish rather than piling on.
  if (startPromise) {
    return startPromise
  }

  if (isTrackingHealthy()) {
    return true
  }

  if (isTrackingActive && trackingInterval !== null) {
    // Interval alive but silent — tear it down and start clean.
    locationLog('🩺 Tracking interval is alive but stale, restarting it')
    stopBackgroundTracking()
  }

  return startBackgroundTracking()
}

// Periodic health check. Only runs while a user is logged in.
export const startTrackingWatchdog = (): void => {
  if (watchdogInterval) {
    return
  }
  locationLog('🩺 Starting tracking watchdog')
  watchdogInterval = setInterval(() => {
    ensureTrackingHealthy().catch((error) => {
      locationWarn('Watchdog check failed:', error)
    })
  }, WATCHDOG_INTERVAL_MS)
}

export const stopTrackingWatchdog = (): void => {
  if (watchdogInterval) {
    locationLog('🩺 Stopping tracking watchdog')
    clearInterval(watchdogInterval)
    watchdogInterval = null
  }
}

// --- Resume handling -------------------------------------------------------
// iOS and Android freeze (and sometimes kill) timers once the PWA goes to the
// background. Continuous recording behind a locked screen is not achievable
// without a native shell — but coming back to the foreground must revive the
// recording immediately, which is what this does.
//
// App.vue also calls ensureTrackingHealthy() at the end of its own resume
// handler, after the token refresh. Both are idempotent; this listener is the
// safety net for the paths where that handler returns early (offline, or while
// on the login / location-required screens).

let resumeListenersAttached = false

const handleAppResume = (): void => {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
    return
  }
  if (!isUserAuthenticated()) {
    return
  }

  locationLog('📲 App resumed, verifying tracking health')

  // The watchdog is a timer too and may have been killed along with the rest.
  startTrackingWatchdog()

  ensureTrackingHealthy().catch((error) => {
    locationWarn('Resume health check failed:', error)
  })
}

export const attachResumeListeners = (): void => {
  if (resumeListenersAttached || typeof document === 'undefined') {
    return
  }
  document.addEventListener('visibilitychange', handleAppResume)
  window.addEventListener('pageshow', handleAppResume)
  resumeListenersAttached = true
}

export const detachResumeListeners = (): void => {
  if (!resumeListenersAttached || typeof document === 'undefined') {
    return
  }
  document.removeEventListener('visibilitychange', handleAppResume)
  window.removeEventListener('pageshow', handleAppResume)
  resumeListenersAttached = false
}

// Debug function to show current state
export const debugTrackingState = (): void => {
  // Try to update global auth state if it's not synchronized
  if (!globalAuthState.isAuthenticated && localStorage.getItem('token')) {
    console.log('🔄 Global auth state not synchronized, setting basic auth state...')
    // Set basic auth state since we have a token
    globalAuthState = {
      isAuthenticated: true,
      user: { id: 1, name: 'User' } // Placeholder user data - will be updated when tracking starts
    }
    console.log('✅ Basic global auth state set')
  }
  
  console.log('🔍 === LOCATION TRACKING DEBUG ===')
  console.log('  - Local Tracking State:', {
    isActive: trackingState.value.isActive,
    error: trackingState.value.error,
    lastUpdate: lastLocationUpdate?.toISOString()
  })
  console.log('  - Global Tracking State:', {
    isActive: isTrackingActive,
    intervalId: trackingInterval
  })
  console.log('  - Global Auth State:', {
    isAuthenticated: globalAuthState.isAuthenticated,
    user: globalAuthState.user ? { id: globalAuthState.user.id, name: globalAuthState.user.name } : null
  })
  console.log('  - Auth Check Result:', {
    isAuthenticated: isUserAuthenticated(),
    user: getUserData() ? { id: getUserData().id, name: getUserData().name } : null
  })
  console.log('  - Current Time:', new Date().toISOString())
  console.log('  - Token in localStorage:', !!localStorage.getItem('token'))
  console.log('  - User data in localStorage:', !!localStorage.getItem('user_data'))
  console.log('  - Token value:', localStorage.getItem('token')?.substring(0, 20) + '...')
  console.log('  - User data value:', getUserData())
  console.log('  - Browser geolocation support:', !!navigator.geolocation)
  console.log('================================')
}

// Test function to manually send a location update
export const testLocationTracking = async (): Promise<boolean> => {
  locationLog('🧪 Testing location tracking...')
  
  if (!isUserAuthenticated()) {
    locationLog('❌ Cannot test: User not authenticated')
    return false
  }
  
  try {
    const position = await getCurrentLocationDirect()
    if (!position) {
      locationLog('❌ Could not get position for test')
      return false
    }
    
    locationLog('📍 Test position obtained:', {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy
    })
    
    const locationData = await createLocationData(position, getUserData().id)
    const success = await sendLocationToServer(locationData)
    
    if (success) {
      locationLog('✅ Test location sent successfully')
      return true
    } else {
      locationLog('❌ Failed to send test location')
      return false
    }
  } catch (error) {
    console.error('❌ Error testing location tracking:', error)
    return false
  }
}

// Main composable function
export const useLocationTracking = () => {
  const { state } = useAuth()
  
  // Watch for authentication changes
  watch(() => state.value.isAuthenticated, (newAuthState, oldAuthState) => {
    locationLog('🔐 Auth state changed:', {
      from: oldAuthState,
      to: newAuthState,
      user: state.value.user ? { id: state.value.user.id, name: state.value.user.name } : null
    })
    
    // Update global auth state whenever auth state changes
    updateGlobalAuthState(state.value)
    
    if (newAuthState && state.value.user) {
      locationLog('👤 User authenticated, starting background tracking')
      startBackgroundTrackingIfNotRunning()
    } else if (!newAuthState) {
      locationLog('👤 User logged out, stopping background tracking')
      stopTrackingWatchdog()
      stopBackgroundTracking()
    }
  }, { immediate: true })
  
  // Watch for user changes
  watch(() => state.value.user, (newUser, oldUser) => {
    locationLog('👤 User data changed:', {
      from: oldUser ? { id: oldUser.id, name: oldUser.name } : null,
      to: newUser ? { id: newUser.id, name: newUser.name } : null
    })
    
    // Update global auth state whenever user data changes
    updateGlobalAuthState(state.value)
    
    if (newUser && state.value.isAuthenticated) {
      locationLog('👤 User data updated, ensuring background tracking is active')
      startBackgroundTrackingIfNotRunning()
    }
  }, { immediate: true })
  
  // Start tracking when component mounts if user is already authenticated
  onMounted(() => {
    locationLog('🏠 Component mounted, checking authentication state')
    
    // Update global auth state on mount
    updateGlobalAuthState(state.value)
    
    if (state.value.isAuthenticated && state.value.user) {
      locationLog('👤 User already authenticated on mount, starting background tracking')
      
      // Warte einen kurzen Moment, um sicherzustellen, dass der Auth-Status vollständig geladen ist
      setTimeout(() => {
        startBackgroundTrackingIfNotRunning()
      }, 300)
    }
  })
  
  // NO onUnmounted stop here — on purpose.
  //
  // Tracking is app-scoped, not component-scoped. This composable is used by
  // App.vue *and* by LoginView / TerminalSelectionView, so an unmount hook meant
  // that leaving the login or terminal screen killed tracking for the whole app.
  // Right after login that is fatal: LoginView fires the start and is unmounted
  // by the redirect a moment later, so the shift began with no tracking at all.
  //
  // Tracking is stopped only when it should be:
  //   - logout / auth loss  → the isAuthenticated watcher above
  //   - permission lost     → App.vue's location permission watcher
  //   - safety net          → the collect interval stops itself as soon as
  //                           isUserAuthenticated() turns false (max 30s later)

  return {
    isActive: computed(() => trackingState.value.isActive),
    error: computed(() => trackingState.value.error),
    lastUpdate: computed(() => lastLocationUpdate),
    startBackgroundTracking: () => startBackgroundTracking(),
    startBackgroundTrackingIfNotRunning: () => startBackgroundTrackingIfNotRunning(),
    stopBackgroundTracking,
    ensureTrackingHealthy,
    startTrackingWatchdog,
    stopTrackingWatchdog,
    attachResumeListeners,
    detachResumeListeners,
    getTrackingStatus,
    debugTrackingState,
    testLocationTracking
  }
} 